import orderBy from "lodash/orderBy";
import take from "lodash/take";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { CustomValuePill, CustomValuePillProps } from "PFComponents/custom_value_pill";
import { useCustomValueToString } from "PFCore/helpers/custom_value";
import { PureCustomValue } from "PFTypes";

import { CustomValuesListCommonProps, DEFAULT_LIMIT } from "./custom_values_list.consts";
import css from "./custom_values_list.module.scss";
import { useCustomValuesVisibility } from "./hooks/use_custom_values_visibility";
import { CustomValuesDivider } from "./parts/custom_values_divider";
import { CustomValuesWrapper } from "./parts/custom_values_wrapper";

export type CustomValuesListProps<T> = Omit<CustomValuesListCommonProps, "customFieldName" | "canEdit"> & {
  customValues: T[];
  injectCustomPillProps?: (customValue: T) => Partial<CustomValuePillProps>;
};

export const CustomValuesList = <T extends PureCustomValue = PureCustomValue>({
  customValues,
  limit = DEFAULT_LIMIT,
  dark = false,
  vertical = false,
  compact,
  maxNameLength,
  customOrder,
  tabIndex,
  injectCustomPillProps,
  onMoreLessButtonClick
}: CustomValuesListProps<T>) => {
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { getCustomValueString } = useCustomValueToString();

  const sortedCustomValues = orderBy(customValues, ...(customOrder ?? [["value"]]));

  const { visibleAvailableCustomValuesCount, showAll, displayShowMoreButton, toggleShowAll } =
    useCustomValuesVisibility({
      availableCustomValuesCount: sortedCustomValues.length,
      missingCustomValuesCount: 0,
      limit
    });

  const handleMoreLessButtonClick = () => {
    toggleShowAll();
    onMoreLessButtonClick?.();
  };

  return (
    <CustomValuesWrapper
      displayShowMoreButton={displayShowMoreButton}
      showAll={showAll}
      dark={dark}
      limit={limit}
      vertical={vertical}
      compact={compact}
      onMoreLessButtonClick={handleMoreLessButtonClick}
      tabIndex={tabIndex}
    >
      {take(sortedCustomValues, visibleAvailableCustomValuesCount).map((customValue, index) => {
        const isLastCustomValue = index === visibleAvailableCustomValuesCount - 1;

        return (
          <span className={css.customValueWithDivider} key={customValue.id}>
            <CustomValuePill
              name={getCustomValueString(customValue)}
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              theme={dark ? "dark" : "light"}
              maxNameLength={maxNameLength}
              showTooltip={false}
              tabIndex={tabIndex}
              {...injectCustomPillProps?.(customValue)}
            />
            {!isLastCustomValue && !vertical && <CustomValuesDivider />}
          </span>
        );
      })}
    </CustomValuesWrapper>
  );
};
